import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, setTenantCurrency, SESSION_EXPIRED_EVENT, SESSION_EXPIRED_MSG } from "@/lib/api";

const AuthCtx = createContext(null);
export const RETURN_TO_KEY = "neec_return_to";
export const SESSION_EXPIRED_KEY = "neec_session_expired";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  useEffect(() => {
    // Fired by the api interceptor when refresh fails. Only a previously signed-in user gets the expired-session flow.
    const onExpired = () => {
      if (!userRef.current) return;
      const path = window.location.pathname + window.location.search;
      if (!path.startsWith("/login")) sessionStorage.setItem(RETURN_TO_KEY, path);
      sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
      setUser(false);
      toast.message(SESSION_EXPIRED_MSG, { id: "session-expired" });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.requires_2fa) return { requires_2fa: true };
    const me = await api.get("/auth/me");
    setUser(me.data);
    return { ok: true };
  };
  const verify2fa = async (body) => {
    await api.post("/auth/2fa/verify", body);
    const me = await api.get("/auth/me");
    setUser(me.data);
  };
  const activate = async (token, password, name) => {
    await api.post(`/public/activate/${token}`, { password, name });
    const me = await api.get("/auth/me");
    setUser(me.data);
  };
  const refreshUser = async () => { const me = await api.get("/auth/me"); setUser(me.data); };
  useEffect(() => {
    if (!user) { setTenantCurrency("USD"); return; }
    api.get("/settings").then((r) => setTenantCurrency(r.data?.currency)).catch(() => {});
  }, [user]);
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    sessionStorage.removeItem(RETURN_TO_KEY);
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    setUser(false);
  };
  return <AuthCtx.Provider value={{ user, login, verify2fa, logout, activate, refreshUser }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
